import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  ownAdsData,
} from '@/mocks/ownAds';
import AdminOwnAdFormModal from './components/AdminOwnAdFormModal';
import type {
  OwnAdsData,
  OwnBanner,
  OwnSponsor,
  OwnEvent,
  OwnContest,
  OwnProgramSponsorship,
  OwnAdsHistoryEntry,
} from '@/mocks/ownAds';

type LoadState = 'loading' | 'ready' | 'error';
type TabKey = 'banners' | 'patrocinadores' | 'eventos' | 'concursos' | 'programas' | 'historial';
type DealType = 'banner' | 'patrocinador' | 'evento' | 'concurso' | 'programa';

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 400 + Math.random() * 200;
    setTimeout(() => {
      if (Math.random() > 0.97) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function formatCurrencyFull(n: number): string {
  return `$${n.toLocaleString('es-AR')}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 animate-pulse">
        <div className="w-48 h-4 bg-background-200 rounded mb-3"></div>
        <div className="w-32 h-8 bg-background-200 rounded mb-4"></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 bg-background-200 rounded-lg"></div>
          ))}
        </div>
      </div>
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 animate-pulse">
        <div className="flex gap-3 mb-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="w-20 h-8 bg-background-200 rounded-lg"></div>
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 bg-background-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Tab Button ── */
function TabButton({ active, icon, label, count, onClick }: { active: boolean; icon: string; label: string; count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
        active
          ? 'bg-emerald-500 text-white'
          : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
      }`}
    >
      <i className={`${icon} text-sm`}></i>
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
        {count}
      </span>
    </button>
  );
}

/* ── Banners Table ── */
function BannersTable({ items }: { items: OwnBanner[] }) {
  const handleClick = useCallback((item: OwnBanner) => {
    Swal.fire({
      title: item.name,
      html: `
        <div style="text-align:left;font-size:12px;line-height:1.9;">
          <p><strong>Cliente:</strong> ${item.client}</p>
          <p><strong>Ubicación:</strong> ${item.location}</p>
          <p><strong>Formato:</strong> ${item.size}</p>
          <p><strong>Precio:</strong> $${item.price.toLocaleString()}/mes</p>
          <p><strong>Vigencia:</strong> ${item.startDate} → ${item.endDate}</p>
          <hr style="margin:10px 0;border:none;border-top:1px solid #eee;" />
          <p><strong>Impresiones:</strong> ${item.impressions.toLocaleString()}</p>
          <p><strong>Clicks:</strong> ${item.clicks.toLocaleString()}</p>
          <p><strong>CTR:</strong> ${item.ctr.toFixed(1)}%</p>
          <p style="color:#059669;font-weight:bold;">Revenue: $${item.revenue.toLocaleString()} (100% tuyo)</p>
        </div>
      `,
      confirmButtonColor: '#059669',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  }, []);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Banner</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Cliente</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ubicación</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Formato</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Vigencia</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Impr.</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">CTR</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ingreso</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} onClick={() => handleClick(item)} className="border-b border-background-200 hover:bg-background-50/50 transition-colors cursor-pointer">
                <td className="py-3 px-4">
                  <span className="text-foreground-800 text-xs font-semibold">{item.name}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-600 text-xs">{item.client}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-[10px]">{item.location}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[10px] font-medium">{item.size}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground-500 text-[10px] font-medium whitespace-nowrap">{item.startDate} → {item.endDate}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-600 text-xs font-medium">{formatNumber(item.impressions)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className={`text-xs font-medium ${item.ctr >= 4.5 ? 'text-emerald-600' : 'text-amber-600'}`}>{item.ctr.toFixed(1)}%</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.revenue)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} onClick={() => handleClick(item)} className="p-4 cursor-pointer hover:bg-background-50/50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="min-w-0">
                <p className="text-foreground-800 text-xs font-semibold">{item.name}</p>
                <p className="text-foreground-400 text-[10px]">{item.client} · {item.location}</p>
              </div>
              <span className="text-emerald-600 text-xs font-bold shrink-0">{formatCurrency(item.revenue)}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-foreground-500">
              <span className="px-1.5 py-0.5 bg-background-200 rounded">{item.size}</span>
              <span>{formatNumber(item.impressions)} imp.</span>
              <span>{item.ctr.toFixed(1)}% CTR</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Sponsors Table ── */
function SponsorsTable({ items }: { items: OwnSponsor[] }) {
  const handleClick = useCallback((item: OwnSponsor) => {
    Swal.fire({
      title: item.name,
      html: `
        <div style="text-align:left;font-size:12px;line-height:1.9;">
          <p><strong>Categoría:</strong> ${item.category}</p>
          <p>${item.description}</p>
          <hr style="margin:10px 0;border:none;border-top:1px solid #eee;" />
          <p><strong>Aporte mensual:</strong> $${item.amount.toLocaleString()}</p>
          <p><strong>Desde:</strong> ${new Date(item.since).toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p><strong>Contacto:</strong> ${item.contactName} · ${item.contactEmail}</p>
        </div>
      `,
      confirmButtonColor: '#059669',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  }, []);

  const statusDot = (status: string) => {
    if (status === 'activo') return <span className="w-2 h-2 rounded-full bg-emerald-500"></span>;
    return <span className="w-2 h-2 rounded-full bg-amber-500"></span>;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((item) => (
        <div
          key={item.id}
          onClick={() => handleClick(item)}
          className="bg-background-50 border border-background-200 rounded-2xl p-4 cursor-pointer hover:border-background-300 transition-colors"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <span className="text-emerald-700 text-xs font-black">{item.logo}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-foreground-800 text-xs font-bold truncate">{item.name}</p>
                {statusDot(item.status)}
              </div>
              <p className="text-foreground-400 text-[10px] mt-0.5 line-clamp-2">{item.description}</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-background-200">
            <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-medium">{item.category}</span>
            <span className="text-emerald-600 text-sm font-bold">{formatCurrency(item.amount)}</span>
            <span className="text-foreground-400 text-[9px]">/mes</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Events Table ── */
function EventsTable({ items }: { items: OwnEvent[] }) {
  const handleClick = useCallback((item: OwnEvent) => {
    Swal.fire({
      title: item.name,
      html: `
        <div style="text-align:left;font-size:12px;line-height:1.9;">
          <p><strong>Sponsor:</strong> ${item.sponsor}</p>
          <p><strong>Fecha:</strong> ${new Date(item.date).toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p><strong>Lugar:</strong> ${item.location}</p>
          <p><strong>Asistentes estimados:</strong> ${item.attendees}</p>
          <hr style="margin:10px 0;border:none;border-top:1px solid #eee;" />
          <p style="color:#059669;font-weight:bold;">Ingreso: $${item.amount.toLocaleString()} (100% tuyo)</p>
        </div>
      `,
      confirmButtonColor: '#059669',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  }, []);

  const statusBadge = (status: string) => {
    if (status === 'confirmado') {
      return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-medium whitespace-nowrap">Confirmado</span>;
    }
    return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-medium whitespace-nowrap">Pendiente</span>;
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Evento</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Sponsor</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Fecha</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Lugar</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Asistentes</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ingreso</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} onClick={() => handleClick(item)} className="border-b border-background-200 hover:bg-background-50/50 transition-colors cursor-pointer">
                <td className="py-3 px-4">
                  <span className="text-foreground-800 text-xs font-semibold">{item.name}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-600 text-xs">{item.sponsor}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-[10px] whitespace-nowrap">{item.date}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-[10px]">{item.location}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-600 text-xs font-medium">{item.attendees}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  {statusBadge(item.status)}
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.amount)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} onClick={() => handleClick(item)} className="p-4 cursor-pointer hover:bg-background-50/50 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <p className="text-foreground-800 text-xs font-semibold">{item.name}</p>
                <p className="text-foreground-400 text-[10px]">{item.sponsor} · {item.date}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusBadge(item.status)}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground-500 text-[10px]">{item.location} · {item.attendees} asistentes</span>
              <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Contests Table ── */
function ContestsTable({ items }: { items: OwnContest[] }) {
  const handleClick = useCallback((item: OwnContest) => {
    Swal.fire({
      title: item.name,
      html: `
        <div style="text-align:left;font-size:12px;line-height:1.9;">
          <p><strong>Sponsor:</strong> ${item.sponsor}</p>
          <p><strong>Premio:</strong> ${item.prize}</p>
          <p><strong>Vigencia:</strong> ${item.startDate} → ${item.endDate}</p>
          <p><strong>Participantes:</strong> ${item.participants.toLocaleString()}</p>
          <hr style="margin:10px 0;border:none;border-top:1px solid #eee;" />
          <p style="color:#059669;font-weight:bold;">Ingreso: $${item.amount.toLocaleString()} (100% tuyo)</p>
        </div>
      `,
      confirmButtonColor: '#059669',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  }, []);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Concurso</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Sponsor</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Premio</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Vigencia</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Participantes</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ingreso</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} onClick={() => handleClick(item)} className="border-b border-background-200 hover:bg-background-50/50 transition-colors cursor-pointer">
                <td className="py-3 px-4">
                  <span className="text-foreground-800 text-xs font-semibold">{item.name}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-600 text-xs">{item.sponsor}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-[10px]">{item.prize}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground-500 text-[10px] whitespace-nowrap">{item.startDate} → {item.endDate}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-600 text-xs font-medium">{item.participants.toLocaleString()}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.amount)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} onClick={() => handleClick(item)} className="p-4 cursor-pointer hover:bg-background-50/50 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <p className="text-foreground-800 text-xs font-semibold">{item.name}</p>
                <p className="text-foreground-400 text-[10px]">{item.sponsor} · {item.prize}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground-500 text-[10px]">{item.startDate} → {item.endDate} · {item.participants.toLocaleString()} particip.</span>
              <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Program Sponsorship Table ── */
function ProgramSponsorshipsTable({ items }: { items: OwnProgramSponsorship[] }) {
  const handleClick = useCallback((item: OwnProgramSponsorship) => {
    Swal.fire({
      title: `${item.programName} × ${item.sponsor}`,
      html: `
        <div style="text-align:left;font-size:12px;line-height:1.9;">
          <p><strong>Programa:</strong> ${item.programName}</p>
          <p><strong>Sponsor:</strong> ${item.sponsor}</p>
          <p><strong>Tipo de espacio:</strong> ${item.slotType}</p>
          <p><strong>Frecuencia:</strong> ${item.frequency}</p>
          <p><strong>Precio:</strong> $${item.price.toLocaleString()}/mes</p>
          <p><strong>Vigencia:</strong> ${item.startDate} → ${item.endDate}</p>
          <hr style="margin:10px 0;border:none;border-top:1px solid #eee;" />
          <p style="color:#059669;font-weight:bold;">Revenue total: $${item.revenue.toLocaleString()} (100% tuyo)</p>
        </div>
      `,
      confirmButtonColor: '#059669',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  }, []);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Programa</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Sponsor</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Espacio</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Frecuencia</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Vigencia</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Precio</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} onClick={() => handleClick(item)} className="border-b border-background-200 hover:bg-background-50/50 transition-colors cursor-pointer">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.status === 'activo' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                    <span className="text-foreground-800 text-xs font-semibold">{item.programName}</span>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-600 text-xs">{item.sponsor}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-[10px]">{item.slotType}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground-500 text-[10px]">{item.frequency}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground-500 text-[10px] whitespace-nowrap">{item.startDate} → {item.endDate}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-600 text-xs font-medium">{formatCurrency(item.price)}</span>
                  <span className="text-foreground-400 text-[9px]">/m</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.revenue)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} onClick={() => handleClick(item)} className="p-4 cursor-pointer hover:bg-background-50/50 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <p className="text-foreground-800 text-xs font-semibold">{item.programName}</p>
                <p className="text-foreground-400 text-[10px]">{item.sponsor} · {item.slotType}</p>
              </div>
              <span className="text-emerald-600 text-xs font-bold shrink-0">{formatCurrency(item.revenue)}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-foreground-500">
              <span>{item.frequency}</span>
              <span>{formatCurrency(item.price)}/mes</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── History Table ── */
function HistoryTable({ items }: { items: OwnAdsHistoryEntry[] }) {
  const typeBadge = (type: string) => {
    const colors: Record<string, string> = {
      Banner: 'bg-sky-50 text-sky-600',
      Patrocinador: 'bg-rose-50 text-rose-600',
      Evento: 'bg-amber-50 text-amber-600',
      Concurso: 'bg-violet-50 text-violet-600',
      Programa: 'bg-emerald-50 text-emerald-600',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${colors[type] || 'bg-background-200 text-foreground-500'}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tipo</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nombre</th>
              <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Cliente</th>
              <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Período</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Impr.</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Clicks</th>
              <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">CTR</th>
              <th className="text-right py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ingreso</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-background-200 hover:bg-background-50/50 transition-colors">
                <td className="py-3 px-4">{typeBadge(item.type)}</td>
                <td className="py-3 px-3">
                  <span className="text-foreground-800 text-xs font-medium">{item.name}</span>
                </td>
                <td className="py-3 px-3">
                  <span className="text-foreground-500 text-xs">{item.client}</span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="text-foreground-500 text-[10px] whitespace-nowrap">{item.startDate} → {item.endDate}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-500 text-xs">{formatNumber(item.impressions)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-500 text-xs">{formatNumber(item.clicks)}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-foreground-500 text-xs">{item.ctr.toFixed(1)}%</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-foreground-600 text-xs font-semibold">{formatCurrency(item.amount)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">
        {items.map((item) => (
          <div key={item.id} className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {typeBadge(item.type)}
              <span className="text-foreground-400 text-[10px]">{item.startDate} → {item.endDate}</span>
            </div>
            <p className="text-foreground-800 text-xs font-semibold">{item.name}</p>
            <p className="text-foreground-400 text-[10px] mt-0.5">{item.client}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                <span>{formatNumber(item.impressions)} imp.</span>
                <span>{formatNumber(item.clicks)} clicks</span>
                <span>{item.ctr.toFixed(1)}% CTR</span>
              </div>
              <span className="text-foreground-600 text-xs font-semibold">{formatCurrency(item.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function AdminOwnAdsPage() {
  const [data, setData] = useState<OwnAdsData | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('banners');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const loadData = useCallback(() => {
    setLoadState('loading');
    setErrorMsg(null);
    simulateLoad(ownAdsData)
      .then((d) => {
        setData(d);
        setLoadState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = useCallback(
    (
      type: DealType,
      item: OwnBanner | OwnSponsor | OwnEvent | OwnContest | OwnProgramSponsorship
    ) => {
      setData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        switch (type) {
          case 'banner':
            next.banners = [item as OwnBanner, ...next.banners];
            break;
          case 'patrocinador':
            next.sponsors = [item as OwnSponsor, ...next.sponsors];
            break;
          case 'evento':
            next.events = [item as OwnEvent, ...next.events];
            break;
          case 'concurso':
            next.contests = [item as OwnContest, ...next.contests];
            break;
          case 'programa':
            next.programSponsorships = [
              item as OwnProgramSponsorship,
              ...next.programSponsorships,
            ];
            break;
        }
        next.activeDeals += 1;
        return next;
      });
      Swal.fire({
        icon: 'success',
        title: 'Acuerdo Creado',
        text: 'El nuevo acuerdo fue agregado exitosamente.',
        timer: 2500,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl text-sm' },
      });
    },
    []
  );

  /* ── Loading ── */
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
              <div className="w-32 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-44 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8">
              <LoadingSkeleton />
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (loadState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
                <i className="ri-error-warning-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Publicidad Propia</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <ErrorState message={errorMsg || 'Error desconocido'} onRetry={loadData} />
          </main>
        </div>
      </div>
    );
  }

  const { totalRevenue, activeDeals, totalImpressions, totalClicks, avgCtr, monthLabel, banners, sponsors, events, contests, programSponsorships, history } = data!;

  const tabs: { key: TabKey; icon: string; label: string; count: number }[] = [
    { key: 'banners', icon: 'ri-image-line', label: 'Banners', count: banners.length },
    { key: 'patrocinadores', icon: 'ri-star-line', label: 'Patrocinadores', count: sponsors.length },
    { key: 'eventos', icon: 'ri-calendar-event-line', label: 'Eventos', count: events.length },
    { key: 'concursos', icon: 'ri-trophy-line', label: 'Concursos', count: contests.length },
    { key: 'programas', icon: 'ri-radio-line', label: 'Programas', count: programSponsorships.length },
    { key: 'historial', icon: 'ri-history-line', label: 'Historial', count: history.length },
  ];

  const sectionTitle = (tab: TabKey) => {
    const map: Record<TabKey, { title: string; subtitle: string }> = {
      banners: { title: 'Banners Publicitarios', subtitle: 'Espacios de display en tu sitio web. 100% del revenue es tuyo.' },
      patrocinadores: { title: 'Patrocinadores Propios', subtitle: 'Marcas que pagan directamente por estar en tu radio. Sin intermediarios.' },
      eventos: { title: 'Eventos Patrocinados', subtitle: 'Eventos donde marcas pagan por presencia de tu radio.' },
      concursos: { title: 'Concursos con Sponsors', subtitle: 'Sorteos y concursos financiados por marcas. Engagement + revenue.' },
      programas: { title: 'Programas con Sponsors', subtitle: 'Menciones, segmentos y cápsulas dentro de tu programación.' },
      historial: { title: 'Historial de Publicidad Propia', subtitle: 'Todos los acuerdos finalizados. 100% Radio, siempre.' },
    };
    return map[tab];
  };

  const current = sectionTitle(activeTab);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Publicidad Propia</h1>
              <p className="text-foreground-400 text-[9px]">{monthLabel} · {formatCurrency(totalRevenue)}</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
            <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Publicidad Propia</h2>
              <p className="text-foreground-400 text-xs">
                {monthLabel} · {activeDeals} acuerdos activos · 100% del revenue para tu radio
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm"></i>
                Nuevo acuerdo
              </button>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* ── HERO: 100% Radio ── */}
            <div className="bg-background-50 border-2 border-emerald-300 rounded-2xl p-5 md:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0">
                      <span className="text-white text-lg font-black">100%</span>
                    </div>
                    <div>
                      <p className="text-emerald-700 text-xs font-bold">Publicidad Propia</p>
                      <p className="text-foreground-800 text-2xl md:text-3xl font-black">{formatCurrencyFull(totalRevenue)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 ml-0">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
                      <i className="ri-checkbox-circle-fill text-sm"></i>
                      100% Radio
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-100 text-foreground-500 text-[11px] font-medium">
                      <i className="ri-close-circle-fill text-sm"></i>
                      0% RFW
                    </span>
                    <span className="text-foreground-400 text-xs">Todo el revenue es tuyo</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:w-[440px] shrink-0">
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-emerald-700 text-lg font-black">{activeDeals}</p>
                    <p className="text-emerald-600 text-[9px] font-medium">Acuerdos activos</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3 text-center">
                    <p className="text-foreground-800 text-lg font-black">{formatNumber(totalImpressions)}</p>
                    <p className="text-foreground-400 text-[9px] font-medium">Impresiones</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3 text-center">
                    <p className="text-foreground-800 text-lg font-black">{formatNumber(totalClicks)}</p>
                    <p className="text-foreground-400 text-[9px] font-medium">Clicks</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-3 text-center">
                    <p className="text-foreground-800 text-lg font-black">{avgCtr.toFixed(1)}%</p>
                    <p className="text-foreground-400 text-[9px] font-medium">CTR</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
              {/* Tab bar */}
              <div className="px-4 py-3 border-b border-background-200 overflow-x-auto">
                <div className="flex items-center justify-between gap-2 min-w-max">
                  <div className="flex items-center gap-1.5">
                    {tabs.map((tab) => (
                      <TabButton
                        key={tab.key}
                        active={activeTab === tab.key}
                        icon={tab.icon}
                        label={tab.label}
                        count={tab.count}
                        onClick={() => setActiveTab(tab.key)}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-sm"></i>
                    Nuevo
                  </button>
                </div>
              </div>

              {/* Section header */}
              <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-store-2-line text-emerald-600 text-base"></i>
                </div>
                <div>
                  <h3 className="text-foreground-800 text-sm font-bold">{current.title}</h3>
                  <p className="text-foreground-400 text-[10px]">{current.subtitle}</p>
                </div>
              </div>

              {/* Tab content */}
              <div className={activeTab === 'banners' ? '' : 'hidden'}>
                <BannersTable items={banners} />
              </div>
              <div className={activeTab === 'patrocinadores' ? '' : 'hidden'}>
                <div className="p-4">
                  <SponsorsTable items={sponsors} />
                </div>
              </div>
              <div className={activeTab === 'eventos' ? '' : 'hidden'}>
                <EventsTable items={events} />
              </div>
              <div className={activeTab === 'concursos' ? '' : 'hidden'}>
                <ContestsTable items={contests} />
              </div>
              <div className={activeTab === 'programas' ? '' : 'hidden'}>
                <ProgramSponsorshipsTable items={programSponsorships} />
              </div>
              <div className={activeTab === 'historial' ? '' : 'hidden'}>
                <HistoryTable items={history} />
              </div>
            </div>

            {/* ── Footer Banner ── */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0">
                  <i className="ri-check-double-line text-white text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-emerald-800 text-sm font-bold">100% tuyo. Siempre.</h3>
                  <p className="text-emerald-700/80 text-xs mt-1">
                    Toda la publicidad que gestionás directamente te deja el <strong className="text-emerald-800">100% del revenue</strong>.{' '}
                    Sin comisiones, sin repartos, sin intermediarios. Cuanto más vendas, más ganás.
                  </p>
                </div>
                <div className="text-center shrink-0">
                  <span className="text-3xl font-black text-emerald-600">{formatCurrency(totalRevenue)}</span>
                  <p className="text-emerald-600 text-[10px] font-bold">este mes</p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>


      <AdminOwnAdFormModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  );
}